import React, { useState } from 'react';
import Layout from '../components/Layout';
import { useConfig } from '../contexts/ConfigContext';
import ConfigPanel from '../components/v3/ConfigPanel';
import { loucaApi } from '../services/loucaApi';

const ConfigPage = () => {
  const { selectedVersion, setSelectedVersion, availableVersions } = useConfig();
  const [v9Config, setV9Config] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const handleConfigChange = (newConfig) => {
    setV9Config(newConfig);
  };

  const handleSaveConfig = async () => {
    if (!v9Config) return;
    
    try {
      setSaving(true);
      setSaveMessage(null);
      
      const response = await loucaApi.getConfigV9();
      if (response.status === 'success') {
        // Mettre à jour la configuration via PUT
        const updateResponse = await fetch('https://louca.certifia.ai/api/v9/config', {
          method: 'PUT',
          headers: {
            'Authorization': 'Bearer louca-certifia-2025',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ config: v9Config }),
        });
        
        if (updateResponse.ok) {
          setSaveMessage({ type: 'success', text: 'Configuration sauvegardée avec succès !' });
        } else {
          throw new Error('Erreur lors de la sauvegarde');
        }
      }
    } catch (err) {
      setSaveMessage({ type: 'error', text: `Erreur: ${err.message}` });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8 pb-24">
        <header className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
          <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Configuration</p>
          <h1 className="text-3xl font-bold text-white mt-2">Paramètres d'analyse</h1>
          <p className="mt-3 text-slate-300">
            Configurez les paramètres V9 et sélectionnez la version du pipeline d'analyse.
          </p>
        </header>

        {/* Section Configuration V9 */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-white">Configuration V9</h2>
            {v9Config && (
              <button
                onClick={handleSaveConfig}
                disabled={saving}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
              >
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            )}
          </div>
          
          {saveMessage && (
            <div className={`p-4 rounded-lg border ${
              saveMessage.type === 'success' 
                ? 'bg-green-500/10 border-green-500/50 text-green-400' 
                : 'bg-red-500/10 border-red-500/50 text-red-400'
            }`}>
              {saveMessage.text}
            </div>
          )}

          <ConfigPanel config={v9Config} onConfigChange={handleConfigChange} />
        </section>

        {/* Section Version du workflow (legacy) */}
        <section className="bg-gradient-to-br from-orange-500/15 via-slate-800 to-slate-900 border border-orange-500/30 rounded-2xl p-6 space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-white mb-2">Version du moteur (Legacy)</h2>
            <p className="text-sm text-orange-200">
              Le choix est mémorisé localement et appliqué lors des prochains uploads.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {availableVersions.map((version) => (
              <label
                key={version.id}
                className={`relative flex flex-col gap-2 rounded-xl border p-5 cursor-pointer transition ${
                  selectedVersion === version.id
                    ? 'border-orange-500/60 bg-orange-500/10 shadow-lg shadow-orange-500/10'
                    : 'border-slate-700 bg-slate-800 hover:border-orange-500/40'
                }`}
              >
                <span className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="version"
                    value={version.id}
                    checked={selectedVersion === version.id}
                    onChange={() => setSelectedVersion(version.id)}
                    className="h-4 w-4 text-orange-500 focus:ring-orange-400"
                  />
                  <span className="text-lg font-semibold text-white">{version.label}</span>
                </span>
                <span className="text-sm text-slate-300">
                  {version.id === 'video_complex_v1'
                    ? "Workflow principal : jusqu'à 4 vidéos, prompts optimisés Gemini 2.5."
                    : version.id === 'video_benchmark_v1'
                    ? 'Benchmark : prompt simple historique pour comparer les résultats.'
                    : 'Images → IA : une seule vidéo extérieure, extraction YOLO + Vision Llama.'}
                </span>
              </label>
            ))}
          </div>
        </section>

        {/* Section Documentation */}
        <section className="grid gap-4 md:grid-cols-3">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-2">Video → IA (prompt complexe)</h3>
            <ul className="space-y-2 text-sm text-slate-300 list-disc list-inside">
              <li>Jusqu&apos;à 4 vidéos couvrant tous les angles.</li>
              <li>Prompt Gemini 2.5 optimisé + sortie complète.</li>
              <li>Détection des vues manquantes et suivi Redis.</li>
            </ul>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-2">Video → IA Bench</h3>
            <ul className="space-y-2 text-sm text-slate-300 list-disc list-inside">
              <li>Prompt simple historique (benchmark interne).</li>
              <li>Comparaison rapide avec les anciennes analyses.</li>
              <li>Mêmes contraintes vidéo que le workflow principal.</li>
            </ul>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-2">Images → IA (YOLO + Vision)</h3>
            <ul className="space-y-2 text-sm text-slate-300 list-disc list-inside">
              <li>Une seule vidéo extérieure obligatoire.</li>
              <li>Extraction d'images clés + analyse Llama Vision.</li>
              <li>Rapport enrichi par synthèse YOLO/LLM.</li>
            </ul>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ConfigPage;
